import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, FieldArray } from "formik";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  orderBy,
  limitToLast,
  arrayRemove,
} from "firebase/firestore";

import { db } from "../../firebase";
import { uploadImage } from "../../utils/imageUpload";

import InputFiled from "Components/Forms/InputForm/InputField";
import TextAreaInput from "Components/Forms/TextAreaForm/TextArea";
import WorkHeaderInfo from "Pages/WorkInformation/WorkHeaderInfo/WorkHeaderInfo";
import ModalRequestAndAddProject from "Components/ModalRequestAndAddProject/ModalRequestAndAddProject";
import HelmetInfo from "Components/HelmetInfo/HelmetInfo";
import ImageUploader from "./ImageUploader";
import ImageUploaderMutli from "./ImageUploaderMutli";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

const AddDoctor = () => {
  const { t } = useTranslation();
  const router = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");

  const [initialValues, setInitialValues] = useState({
    Title: "",
    Name: "",
    Biography: "",
    facebook: "",
    twitter: "",
    linkedin: "",
    work: "",
    feat1: [],
    feat2: [],
    order: 0,
  });

  const [loading, setLoading] = useState(false);
  const [Fetchloading, setFetchloading] = useState(false);
  const [showModal, setShowModalSuccess] = useState(false);

  const [firstImage, setfirstImage] = useState();
  const [secondImage, setsecondImage] = useState();
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Existing image URLs
  const [existingFirstImageUrl, setExistingFirstImageUrl] = useState(null);
  const [existingSecondImageUrl, setExistingSecondImageUrl] = useState(null);
  const [existingAdditionalImages, setExistingAdditionalImages] = useState([]);

  const validationSchema = Yup.object().shape({
    Title: Yup.string().required(t("required")),
    Name: Yup.string().required(t("required")),
  });

  const handleCloseModal = () => {
    setShowModalSuccess(false);
    router("/home");
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    let firstImageUrl = existingFirstImageUrl;
    let secondImageUrl = existingSecondImageUrl;
    let newOrder = values.order || 0;

    try {
      if (!editId) {
        // Get last order
        const doctorsCollectionRef = collection(db, "OurDoctors", "Doctors", "Cards");
        const q = query(doctorsCollectionRef, orderBy("order", "desc"), limitToLast(1));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const lastDoctor = querySnapshot.docs[0].data();
          newOrder = (lastDoctor.order || 0) + 1;
        }
      }

      // Upload first image
      if (firstImage?.file) {
        firstImageUrl = await uploadImage(firstImage.file);
      }

      // Upload second image
      if (secondImage?.file) {
        secondImageUrl = await uploadImage(secondImage.file);
      }

      // Upload multiple images
      const additionalImagesUrls = [...existingAdditionalImages];
      for (const file of selectedFiles) {
        if (file.file) {
          const url = await uploadImage(file.file);
          additionalImagesUrls.push(url);
        }
      }

      // Create document
      const docRef = collection(db, "OurDoctors", "Doctors", "Cards");
      const dataToSave = {
        ...values,
        order: newOrder,
        firstImageUrl,
        secondImageUrl,
        additionalImages: additionalImagesUrls,
      };

      if (editId) {
        const docToUpdate = doc(db, "OurDoctors", "Doctors", "Cards", editId);
        await updateDoc(docToUpdate, dataToSave);
        toast.success(t("DoctorUpdatedSuccessfully") || "Doctor updated successfully", {
          autoClose: 2000,
          position: toast.POSITION.TOP_CENTER,
        });
      } else {
        await addDoc(docRef, dataToSave);
        toast.success(t("DoctorAddedSuccessfully"), {
          autoClose: 2000,
          position: toast.POSITION.TOP_CENTER,
        });
      }

      router("/AllDoctors");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t("FailedToAddDoctor"), {
        autoClose: 2000,
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editId) {
      const fetchDoctor = async () => {
        try {
          setFetchloading(true);
          const docRef = doc(db, "OurDoctors", "Doctors", "Cards", editId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setInitialValues({
              Title: data.Title || "",
              Name: data.Name || "",
              Biography: data.Biography || "",
              facebook: data.facebook || "",
              twitter: data.twitter || "",
              linkedin: data.linkedin || "",
              work: data.work || "",
              feat1: data.feat1 || [],
              feat2: data.feat2 || [],
              order: data.order || 0,
            });
            setExistingFirstImageUrl(data.firstImageUrl || null);
            setExistingSecondImageUrl(data.secondImageUrl || null);
            setExistingAdditionalImages(data.additionalImages || []);
          }
        } catch (error) {
          console.error("Error fetching doctor:", error);
        } finally {
          setFetchloading(false);
        }
      };
      fetchDoctor();
    }
  }, [editId]);

  return (
    <>
      <HelmetInfo titlePage={t("addNewDoctor")} />
      <ModalRequestAndAddProject showModal={showModal} handleCloseModal={handleCloseModal} />

      <div className="project-management-request all-main">
        <WorkHeaderInfo title={t("add New Doctor")} />
        <div className="form-project-management">
          {!Fetchloading && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, handleChange, handleBlur }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-6 mb-4">
                      <InputFiled label={t("title")} name="Title" type="text" onChange={handleChange} value={values.Title} />
                    </div>
                    <div className="col-lg-6 mb-4">
                      <InputFiled label={t("name")} name="Name" type="text" onChange={handleChange} value={values.Name} />
                    </div>
                    <div className="col-lg-6 mb-4">
                      <InputFiled label={t("facebook")} name="facebook" type="text" onChange={handleChange} value={values.facebook} />
                    </div>
                    <div className="col-lg-6 mb-4">
                      <InputFiled label={t("twitter")} name="twitter" type="text" onChange={handleChange} value={values.twitter} />
                    </div>
                    <div className="col-lg-6 mb-4">
                      <InputFiled label={t("linkedin")} name="linkedin" type="text" onChange={handleChange} value={values.linkedin} />
                    </div>

                    <div className="col-lg-12 mb-4">
                      <h4>{t("Biography")}</h4>
                      <textarea name="Biography" rows={4} style={{ width: "100%" }} onChange={handleChange} value={values.Biography} />
                    </div>

                    <div className="col-lg-12 mb-4">
                      <h4>{t("Work Expreince")}</h4>
                      <TextAreaInput name="work" type="text" onChange={handleChange} value={values.work} />
                    </div>

                    <div className="col-12">
                      <h4>{t("First Features")}</h4>
                      <FieldArray name="feat1">
                        {({ push, remove }) => (
                          <>
                            {values.feat1.map((feat, i) => (
                              <div className="row" key={i}>
                                <div className="col-10">
                                  <InputFiled name={`feat1[${i}]`} value={feat} onChange={handleChange} />
                                </div>
                                <div className="col-2">
                                  <button type="button" className="btn-main trashbtn mt-3 w-100" onClick={() => remove(i)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button type="button" className="btn-main mt-3" onClick={() => push("")}>
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </>
                        )}
                      </FieldArray>
                    </div>

                    <div className="col-12">
                      <h4>{t("Second Features")}</h4>
                      <FieldArray name="feat2">
                        {({ push, remove }) => (
                          <>
                            {values.feat2.map((feat, i) => (
                              <div className="row" key={i}>
                                <div className="col-10">
                                  <InputFiled name={`feat2[${i}]`} value={feat} onChange={handleChange} />
                                </div>
                                <div className="col-2">
                                  <button type="button" className="btn-main trashbtn mt-3 w-100" onClick={() => remove(i)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                </div>
                              </div>
                            ))}
                            <button type="button" className="btn-main mt-3" onClick={() => push("")}>
                              <FontAwesomeIcon icon={faPlus} />
                            </button>
                          </>
                        )}
                      </FieldArray>
                    </div>

                    <ImageUploader setFile={setfirstImage} title="upload Home image" />
                    {existingFirstImageUrl && !firstImage && (
                      <div className="text-center mb-3">
                        <img src={existingFirstImageUrl} alt="Current Home" style={{ width: "100px", height: "auto" }} />
                      </div>
                    )}
                    <ImageUploader setFile={setsecondImage} title="upload Normal image" />
                    {existingSecondImageUrl && !secondImage && (
                      <div className="text-center mb-3">
                        <img src={existingSecondImageUrl} alt="Current Normal" style={{ width: "100px", height: "auto" }} />
                      </div>
                    )}
                    <ImageUploaderMutli setFile={setSelectedFiles} title="upload Certifcates image" />
                    {existingAdditionalImages.length > 0 && (
                      <div className="text-center mb-3">
                        <h6>Current Certificates:</h6>
                        <div className="d-flex justify-content-center gap-2 flex-wrap">
                          {existingAdditionalImages.map((url, i) => (
                            <div key={i} className="position-relative">
                              <img src={url} alt={`Certificate ${i+1}`} style={{ width: "80px", height: "auto" }} />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                style={{ padding: "0 4px" }}
                                onClick={() => setExistingAdditionalImages(existingAdditionalImages.filter((_, idx) => idx !== i))}
                              >
                                &times;
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <button className="btn-main w-100 mt-4" type="submit" disabled={loading}>
                      {loading ? <span className="loader" /> : (editId ? t("Edit Doctor") || "Edit Doctor" : t("Add Doctor"))}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </>
  );
};

export default AddDoctor;
